import React, { useMemo, CSSProperties } from 'react';
import cn from 'classnames';
import { Button } from '@heroui/react';
import { getResponsiveStyle } from '@/shared/lib/utils/getResponsiveStyle';
import { SvgType, ResponsiveSize } from '@/shared/types/common';
import { Svg } from '@/shared/ui/Svg';

export type AppButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  className?: string;
  fullWidth?: boolean;
  type?: string;
  color?: 'yellow' | 'black' | 'white' | 'red' | 'grey' | 'blue';
  children: React.ReactNode;
  onPress?: () => void;
  height?: ResponsiveSize;
  width?: ResponsiveSize;
  startIcon?: SvgType;
  endIcon?: SvgType;
  isLoading?: boolean;
  disabled?: boolean;
};

export const AppButton: React.FC<AppButtonProps> = (props) => {
  const {
    color,
    className,
    fullWidth,
    type = 'button',
    children,
    onPress,
    height,
    width,
    startIcon: StartIcon,
    endIcon: EndIcon,
    isLoading = false,
    disabled = false,
  } = props;

  const style = useMemo(() => {
    let style: CSSProperties = {};
    if (width) {
      style = getResponsiveStyle(width, 'width', style);
    }
    if (height) {
      style = getResponsiveStyle(height, 'height', style);
    }
    return style;
  }, [width, height]);

  const customClassName = useMemo(
    () =>
      cn(
        'transition-all duration-300 ease-in-out',
        'flex items-center justify-center cursor-pointer gap-2',
        'min-w-[140px] h-11 px-4 rounded-xl',
        'text-base font-medium',
        'border',
        {
          'cursor-not-allowed opacity-60': disabled && !isLoading,
          'cursor-wait': isLoading,
          'cursor-pointer': !disabled && !isLoading,
          'w-full min-w-auto': fullWidth,
          'bg-yellow-primary text-black border-yellow-primary': color === 'yellow' && !disabled,
          'hover:bg-yellow-secondary hover:border-yellow-secondary':
            color === 'yellow' && !disabled && !isLoading,
          'bg-black text-white border-black': color === 'black' && !disabled,
          'hover:bg-black/90 hover:border-black/90': color === 'black' && !disabled && !isLoading,
          'bg-grey-brand text-black border-0': color === 'grey' && !disabled,
          'hover:bg-grey-100': color === 'grey' && !disabled && !isLoading,
          'bg-white text-black border-black transition-duration-500': color === 'white',
          'hover:bg-black hover:text-white hover:border-black':
            color === 'white' && !disabled && !isLoading,
          'bg-red text-white border-red': color === 'red' && !disabled,
          'hover:bg-red-600 hover:border-red-600 active:bg-red-700 active:border-red-700':
            color === 'red' && !disabled && !isLoading,
          'bg-[#211D55] text-white border-[#211D55]': color === 'blue' && !disabled,
          'hover:bg-[#2a2568] hover:border-[#2a2568] active:bg-[#1a1744] active:border-[#1a1744]':
            color === 'blue' && !disabled && !isLoading,
        },
      ),
    [color, fullWidth, disabled, isLoading],
  );
  
  return (
    <Button
      className={cn(customClassName, className)}
      onPress={onPress}
      style={style}
      type={type}
      disabled={disabled || isLoading}
    >
      {isLoading ? (
        'Loading...'
      ) : (
        <>
          {StartIcon && <Svg Icon={StartIcon} />}
          {children}
          {EndIcon && <Svg Icon={EndIcon} />}
        </>
      )}
    </Button>
  );
};
